import { useQuery } from "@apollo/client/react";
import { Stack, styled } from "@mui/material";
import { DataGridProProps } from "@mui/x-data-grid-pro";
import { format } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { SetURLSearchParams, useSearchParams } from "react-router-dom";
import { GqlError } from "../../components/common/GqlError";
import {
  Event,
  EventsDocument,
  EventsQuery,
  EventsQueryVariables,
  ExportDeclarationsDocument,
  ExportDeclarationsQuery,
  ExportDeclarationsQueryVariables,
  PublishedSourceBundlesDocument,
  PublishedSourceBundlesQuery,
  PublishedSourceBundlesQueryVariables,
} from "../../graphql/types";
import { setOrRemoveRangeParam, setOrRemoveSearchParam } from "../../lib/Util";
import {
  ExportFilter,
  ImportFilter,
  SourceNode,
  toProjectImportNodes,
  toTreeExportNodes,
} from "../common/FilterPublishedArtifacts";
import { createDefaultEventLogFilters } from "../EventLog/Filters";
import { IEventLogFilters } from "../EventLog/Filters/types";
import { addChildToNode } from "../Filters/Common";
import { EventFilters, isFilteringEvents } from "./EventFilters";
import { EventsDataGrid } from "./EventsDataGrid";

const Container = styled(Stack)({
  width: "100%",
  height: "80vh",
  minHeight: "400px",
  minWidth: "200px",
  borderRadius: "8px",
});

const DATE_FORMAT = "yyyy-MM-dd";
const resolveValue = (value: string | undefined, paramName: string, searchParams: URLSearchParams) =>
  value || searchParams.get(paramName) || undefined;

const updateSearchParams = (
  searchParams: URLSearchParams,
  filter: IEventLogFilters,
  setSearchParams: SetURLSearchParams
) => {
  const { search, severity, dateRange, withComments, downloadName, uploadName, reportRange, projects } = filter;
  const updatedParams = new URLSearchParams(searchParams.toString());
  setOrRemoveSearchParam("search", search, updatedParams);
  setOrRemoveSearchParam("severity", severity?.join(","), updatedParams);
  setOrRemoveRangeParam("reportDatetimeStart", "reportDatetimeEnd", updatedParams, reportRange);
  setOrRemoveRangeParam("createDatetimeStart", "createDatetimeEnd", updatedParams, dateRange);
  setOrRemoveSearchParam("withComments", withComments?.toString(), updatedParams);
  setOrRemoveSearchParam("projects", projects?.join(","), updatedParams);
  setOrRemoveSearchParam("downloadName", downloadName?.join(","), updatedParams);
  setOrRemoveSearchParam("uploadName", uploadName?.join(","), updatedParams);
  if (searchParams.toString() !== updatedParams.toString()) {
    setSearchParams(updatedParams);
  }
};

const matchesFilterBundles = (filterValue: ImportFilter, filter: IEventLogFilters) =>
  (filterValue.project && filter.projects?.includes(filterValue.project)) ||
  (filterValue.importName && filter.downloadName?.includes(filterValue.importName));

const matchesUpload = (filterValue: ExportFilter, filter: IEventLogFilters) =>
  (filterValue.project && filter.projects?.includes(filterValue.project)) ||
  (filterValue.exportName && filter.uploadName?.includes(filterValue.exportName));

const EventsDataGridWithQuery = ({
  variables,
  disableDataGridFilter,
  useDefaultDateRange,
}: {
  variables?: Partial<IEventLogFilters>;
  disableDataGridFilter?: boolean;
  useDefaultDateRange?: boolean;
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filter, setFilter] = useState<IEventLogFilters>(
    createDefaultEventLogFilters({
      search: resolveValue(variables?.search, "search", searchParams),
      severity: variables?.severity || searchParams.get("severity")?.split(",") || undefined,
      reportRange: {
        start:
          (variables?.reportRange?.start && format(variables.reportRange.start, DATE_FORMAT)) ||
          searchParams.get("reportDatetimeStart") ||
          undefined,
        end:
          (variables?.reportRange?.end && format(variables.reportRange.end, DATE_FORMAT)) ||
          searchParams.get("reportDatetimeEnd") ||
          undefined,
      },
      dateRange: {
        start:
          (variables?.dateRange?.start && format(variables.dateRange.start, DATE_FORMAT)) ||
          searchParams.get("createDatetimeStart") ||
          undefined,
        end:
          (variables?.dateRange?.end && format(variables.dateRange.end, DATE_FORMAT)) ||
          searchParams.get("createDatetimeEnd") ||
          undefined,
      },
      withComments:
        variables?.withComments || searchParams.get("withComments")
          ? searchParams.get("withComments") === "true"
          : undefined,
      downloadName: variables?.downloadName || searchParams.get("downloadName")?.split(",") || undefined,
      uploadName: variables?.uploadName || searchParams.get("uploadName")?.split(",") || undefined,
      projects: variables?.projects || searchParams.get("projects")?.split(",") || undefined,
      useDefaultDateRange: useDefaultDateRange,
    })
  );
  const [prevInputsKey, setPrevInputsKey] = useState(
    () => `${searchParams.toString()}|${useDefaultDateRange}|${JSON.stringify(variables)}`
  );

  useEffect(() => {
    updateSearchParams(searchParams, filter, setSearchParams);
  }, [filter, searchParams, setSearchParams]);

  const publishedSourceBundleQuery = useQuery<PublishedSourceBundlesQuery, PublishedSourceBundlesQueryVariables>(
    PublishedSourceBundlesDocument
  );
  const exportDeclarationsQuery = useQuery<ExportDeclarationsQuery, ExportDeclarationsQueryVariables>(
    ExportDeclarationsDocument
  );

  if (variables) {
    const currentInputsKey = `${searchParams.toString()}|${useDefaultDateRange}|${JSON.stringify(variables)}`;
    if (currentInputsKey !== prevInputsKey) {
      setPrevInputsKey(currentInputsKey);
      setFilter(
        createDefaultEventLogFilters({
          search: resolveValue(variables?.search, "search", searchParams),
          severity: variables?.severity || searchParams.get("severity")?.split(",") || undefined,
          reportRange: {
            start:
              (variables?.reportRange?.start && format(variables.reportRange.start, DATE_FORMAT)) ||
              searchParams.get("reportDatetimeStart") ||
              undefined,
            end:
              (variables?.reportRange?.end && format(variables.reportRange.end, DATE_FORMAT)) ||
              searchParams.get("reportDatetimeEnd") ||
              undefined,
          },
          dateRange: {
            start:
              (variables?.dateRange?.start && format(variables.dateRange.start, DATE_FORMAT)) ||
              searchParams.get("createDatetimeStart") ||
              undefined,
            end:
              (variables?.dateRange?.end && format(variables.dateRange.end, DATE_FORMAT)) ||
              searchParams.get("createDatetimeEnd") ||
              undefined,
          },
          withComments:
            variables?.withComments || searchParams.get("withComments")
              ? searchParams.get("withComments") === "true"
              : undefined,
          downloadName: variables?.downloadName || searchParams.get("downloadName")?.split(",") || undefined,
          uploadName: variables?.uploadName || searchParams.get("uploadName")?.split(",") || undefined,
          projects: variables?.projects || searchParams.get("projects")?.split(",") || undefined,
          useDefaultDateRange: useDefaultDateRange,
        })
      );
    }
  }

  const createdAfter = filter.dateRange?.start ? format(filter.dateRange.start, DATE_FORMAT) : null;
  const createdBefore = filter.dateRange?.end ? format(filter.dateRange.end, DATE_FORMAT) : null;
  const reportAfter = filter.reportRange?.start ? format(filter.reportRange.start, DATE_FORMAT) : null;
  const reportBefore = filter.reportRange?.end ? format(filter.reportRange.end, DATE_FORMAT) : null;
  const queryVariables = {
    severities: filter.severity,
    search: filter.search,
    first: 50,
    report_datetime_after: reportAfter,
    report_datetime_before: reportBefore,
    created_after: createdAfter,
    created_before: createdBefore,
    downloadNames: filter.downloadName,
    uploadNames: filter.uploadName,
    projectNames: filter.projects,
  };
  const { data, loading, error, fetchMore } = useQuery<EventsQuery, EventsQueryVariables>(EventsDocument, {
    fetchPolicy: "cache-and-network",
    variables: queryVariables,
    notifyOnNetworkStatusChange: true,
  });

  const onRowsScrollEnd: DataGridProProps["onRowsScrollEnd"] = () => {
    if (!data?.events?.pageInfo.hasNextPage) return;
    fetchMore({
      variables: {
        after: data?.events?.pageInfo?.endCursor || undefined,
      },
    });
  };
  const events = data?.events?.edges?.map((edge) => edge.node as Event) || [];

  const { selectedNodes, nonSelectedNodes } = useMemo(() => {
    let nodes: SourceNode[] = [];
    const nonSelected: SourceNode[] = [];
    const selected: SourceNode[] = [];
    if (publishedSourceBundleQuery.data) {
      const bundles = publishedSourceBundleQuery.data.source_bundles?.edges.map((edge) => edge.node) ?? [];
      nodes = toProjectImportNodes(bundles, true, true);
    }
    if (exportDeclarationsQuery.data) {
      const exports = exportDeclarationsQuery.data.export_declarations?.edges.map((edge) => edge.node);
      if (exports) {
        nodes = nodes.concat(toTreeExportNodes(exports, true, true));
      }
    }
    nodes.sort((a, b) => a.title.localeCompare(b.title));

    nodes.forEach((node: SourceNode) => {
      const isFromDownloads = "importName" in node.filterValue;
      const filterValue = isFromDownloads ? (node.filterValue as ImportFilter) : (node.filterValue as ExportFilter);

      const isMatching = isFromDownloads
        ? matchesFilterBundles(filterValue as ImportFilter, filter)
        : matchesUpload(filterValue as ExportFilter, filter);

      if (node.children.length === 0) {
        // If the node has no children, we can add it directly to the selected or non-selected nodes
        // Otherwise it will be added to the selected or non-selected nodes when we process its children
        (isMatching ? selected : nonSelected).push(node);
      }

      node.children.forEach((child) => {
        const isFromDownloads = "importName" in child.filterValue;
        if (
          isFromDownloads
            ? matchesFilterBundles(child.filterValue as ImportFilter, filter)
            : matchesUpload(child.filterValue as ExportFilter, filter)
        ) {
          addChildToNode(selected, child, node);
        } else {
          addChildToNode(nonSelected, child, node);
        }
      });
    });
    return { selectedNodes: selected, nonSelectedNodes: nonSelected };
  }, [publishedSourceBundleQuery.data, exportDeclarationsQuery.data, filter]);
  return (
    <>
      {error ? (
        <GqlError error={error} />
      ) : (
        <>
          {disableDataGridFilter ? null : (
            <EventFilters
              filter={filter}
              setFilter={setFilter}
              selectedNodesInFilter={selectedNodes}
              nonSelectedNodesInFilter={nonSelectedNodes}
              loading={publishedSourceBundleQuery.loading || exportDeclarationsQuery.loading}
              queryVariables={queryVariables}
            />
          )}
          <Container>
            <EventsDataGrid
              rows={events}
              onRowsScrollEnd={onRowsScrollEnd}
              loading={loading}
              isFiltering={isFilteringEvents(filter)}
            />
          </Container>
        </>
      )}
    </>
  );
};

export { EventsDataGridWithQuery };
